import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { adminAPI, servicesAPI, tariffsAPI, ordersAPI, countriesAPI, settingsAPI } from '../lib/api';
import { formatCurrency, formatShortDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Settings,
  Package,
  DollarSign,
  Building,
  ShoppingCart,
  Download,
  Plus,
  Edit,
  Trash2,
  Loader2,
  TrendingUp,
  Users,
  FileText,
  Globe,
  Mail,
  Save,
  Upload,
  CheckCircle,
  BookOpen,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

// Plastic packaging materials only for AfriCycle Zambia
const PLASTIC_MATERIAL_TYPES = [
  'PET - transparent/light',
  'PET - dark colors',
  'HDPE',
  'PP transparent',
  'LDPE - transparent/white',
  'LDPE - other colours'
];

// WEEE material types
const WEEE_MATERIAL_TYPES = [
  'Temperature Exchange Equipment',
  'Screens and Monitors',
  'Lamps',
  'Large Equipment',
  'Small Equipment',
  'IT and Telecommunication Equipment'
];

// All material types combined
const MATERIAL_TYPES = [...PLASTIC_MATERIAL_TYPES, ...WEEE_MATERIAL_TYPES];

// 2026 only
const YEARS = [2026];

// Order Detail Panel Component for Admin
function OrderDetailPanel({ order, onUpdate, token }) {
  const [status, setStatus] = useState(order.status);
  const [updating, setUpdating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  const handleStatusUpdate = async () => {
    setUpdating(true);
    try {
      await fetch(`${API_BASE}/api/orders/${order.id}/status`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      });
      toast.success('Order status updated');
      onUpdate();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      await fetch(`${API_BASE}/api/orders/${order.id}/payment-confirmation`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      toast.success('Payment confirmation uploaded');
      onUpdate();
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const downloadReceipt = () => {
    window.open(`${API_BASE}/api/orders/${order.id}/receipt?token=${token}`, '_blank');
  };

  const downloadPaymentConfirmation = () => {
    window.open(`${API_BASE}/api/orders/${order.id}/payment-confirmation?token=${token}`, '_blank');
  };

  return (
    <div className="space-y-6 py-4">
      {/* Order Summary */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-sm text-[#6B7280]">Order Number</p>
          <p className="font-semibold text-[#056839]">{order.order_number}</p>
        </div>
        <div>
          <p className="text-sm text-[#6B7280]">Date</p>
          <p className="text-[#3F3F3F]">{order.created_at?.slice(0, 10)}</p>
        </div>
        <div>
          <p className="text-sm text-[#6B7280]">Company</p>
          <p className="text-[#3F3F3F]">{order.company_details?.company_name}</p>
        </div>
        <div>
          <p className="text-sm text-[#6B7280]">Contact</p>
          <p className="text-[#3F3F3F]">{order.company_details?.contact_name}</p>
        </div>
      </div>

      {/* Services */}
      <div className="border-t border-gray-200 pt-4">
        <p className="text-sm font-semibold text-[#056839] mb-2">Ordered Services:</p>
        {order.items?.map((item, idx) => (
          <div key={idx} className="flex justify-between py-1">
            <span className="text-[#3F3F3F]">{item.service_name}</span>
            <span className="font-semibold text-[#056839]">${item.total_price?.toFixed(2)}</span>
          </div>
        ))}
        <div className="flex justify-between pt-2 border-t border-gray-100 mt-2">
          <span className="text-[#6B7280]">Subtotal</span>
          <span className="text-[#3F3F3F]">${order.subtotal?.toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[#6B7280]">VAT (16%)</span>
          <span className="text-[#3F3F3F]">${order.tax?.toFixed(2)}</span>
        </div>
        <div className="flex justify-between pt-2 border-t border-gray-200">
          <span className="font-semibold text-[#056839]">Total</span>
          <span className="font-bold text-xl text-[#056839]">${order.total?.toFixed(2)}</span>
        </div>
      </div>

      {/* Status Update */}
      <div className="border-t border-gray-200 pt-4">
        <p className="text-sm font-semibold text-[#056839] mb-2">Order Status:</p>
        <div className="flex gap-3">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending_confirmation">Awaiting review</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            onClick={handleStatusUpdate} 
            className="btn-primary"
            disabled={updating || status === order.status}
          >
            {updating ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
            Update Status
          </Button>
        </div>
      </div>

      {/* Payment Confirmation Upload */}
      <div className="border-t border-gray-200 pt-4">
        <p className="text-sm font-semibold text-[#056839] mb-2">Payment Confirmation:</p>
        {order.payment_confirmation_file ? (
          <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-green-700">File uploaded</p>
              <p className="text-xs text-green-600">{order.payment_confirmation_file.filename}</p>
            </div>
            <Button variant="outline" size="sm" onClick={downloadPaymentConfirmation}>
              <Download className="h-4 w-4 mr-1" /> Download
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <input
              type="file"
              accept="image/*,application/pdf"
              onChange={handleFileUpload}
              className="hidden"
              id="payment-file-upload"
            />
            <label htmlFor="payment-file-upload">
              <Button variant="outline" className="cursor-pointer" asChild disabled={uploading}>
                <span>
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
                  Upload Payment Confirmation
                </span>
              </Button>
            </label>
            <p className="text-xs text-[#6B7280]">Image or PDF</p>
          </div>
        )}
      </div>

      {/* Download Receipt */}
      <div className="border-t border-gray-200 pt-4">
        <Button variant="outline" onClick={downloadReceipt}>
          <Download className="h-4 w-4 mr-2" />
          Download Order Receipt
        </Button>
      </div>
    </div>
  );
}

export default function AdminPanel() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // Data
  const [stats, setStats] = useState(null);
  const [services, setServices] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [orders, setOrders] = useState([]);
  const [countries, setCountries] = useState([]);
  const [settings, setSettings] = useState({ contact_email: '' });
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Knowledge content
  const [knowledgeContent, setKnowledgeContent] = useState([]);
  const [knowledgeDialog, setKnowledgeDialog] = useState(false);
  const [editingKnowledge, setEditingKnowledge] = useState(null);
  const [knowledgeForm, setKnowledgeForm] = useState({
    title: '',
    summary: '',
    body: '',
    content_type: 'news',
    category: '',
    publish_date: new Date().toISOString().split('T')[0],
    period_label: '',
    status: 'draft'
  });
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  
  // Dialogs
  const [serviceDialog, setServiceDialog] = useState(false);
  const [tariffDialog, setTariffDialog] = useState(false);
  const [countryDialog, setCountryDialog] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [editingTariff, setEditingTariff] = useState(null);
  const [editingCountry, setEditingCountry] = useState(null);

  // Forms
  const [serviceForm, setServiceForm] = useState({
    name: '', description: '', price: 0, category: 'compliance', features: '', is_calculator_based: false
  });
  const [tariffForm, setTariffForm] = useState({
    country: 'Zambia', category: 'plastic', material_type: 'PET - transparent/light', rate_per_tonne: '', year: 2026, is_pending: false
  });
  const [countryForm, setCountryForm] = useState({
    name: '', code: '', vat_rate: 0
  });

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, servicesRes, tariffsRes, companiesRes, ordersRes, countriesRes, settingsRes] = await Promise.all([
        adminAPI.getStats(),
        servicesAPI.getAll(),
        tariffsAPI.getAll(),
        adminAPI.getCompanies(),
        ordersAPI.getAll(),
        countriesAPI.getAll(),
        settingsAPI.get()
      ]);
      
      setStats(statsRes.data);
      setServices(servicesRes.data);
      setTariffs(tariffsRes.data);
      setCompanies(companiesRes.data);
      setOrders(ordersRes.data);
      setCountries(countriesRes.data);
      setSettings(settingsRes.data);
      
      // Load knowledge content
      await loadKnowledgeContent();
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };
  
  const loadKnowledgeContent = async () => {
    try {
      const API_BASE = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_BASE}/api/admin/knowledge`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setKnowledgeContent(data);
      }
    } catch (error) {
      console.error('Failed to load knowledge content');
    }
  };

  const handleSaveService = async () => {
    try {
      const data = {
        ...serviceForm,
        features: serviceForm.features.split('\n').filter(f => f.trim())
      };
      
      if (editingService) {
        await servicesAPI.update(editingService.id, data);
        toast.success('Service updated');
      } else {
        await servicesAPI.create(data);
        toast.success('Service created');
      }
      
      setServiceDialog(false);
      setEditingService(null);
      setServiceForm({ name: '', description: '', price: 0, category: 'compliance', features: '', is_calculator_based: false });
      loadAllData();
    } catch (error) {
      toast.error('Failed to save service');
    }
  };

  const handleDeleteService = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await servicesAPI.delete(id);
      toast.success('Service deleted');
      loadAllData();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setServiceForm({
      name: service.name,
      description: service.description,
      price: service.price,
      category: service.category,
      features: service.features.join('\n'),
      is_calculator_based: service.is_calculator_based
    });
    setServiceDialog(true);
  };

  const handleSaveTariff = async () => {
    try {
      // Prepare data - handle pending fees
      const data = {
        ...tariffForm,
        rate_per_tonne: tariffForm.is_pending ? null : (parseFloat(tariffForm.rate_per_tonne) || 0)
      };
      
      if (editingTariff) {
        await tariffsAPI.update(editingTariff.id, data);
        toast.success('Tariff updated');
      } else {
        await tariffsAPI.create(data);
        toast.success('Tariff created');
      }
      
      setTariffDialog(false);
      setEditingTariff(null);
      setTariffForm({ country: 'Zambia', category: 'plastic', material_type: 'PET - transparent/light', rate_per_tonne: '', year: 2026, is_pending: false });
      loadAllData();
    } catch (error) {
      toast.error('Failed to save tariff');
    }
  };

  const handleEditTariff = (tariff) => {
    setEditingTariff(tariff);
    setTariffForm({
      country: tariff.country,
      category: tariff.category || 'plastic',
      material_type: tariff.material_type,
      rate_per_tonne: tariff.rate_per_tonne !== null ? tariff.rate_per_tonne : '',
      year: tariff.year,
      is_pending: tariff.is_pending || false
    });
    setTariffDialog(true);
  };

  const handleSaveCountry = async () => {
    if (!countryForm.name || !countryForm.code) {
      toast.error('Please fill in country name and code');
      return;
    }
    try {
      if (editingCountry) {
        await countriesAPI.update(editingCountry.id, countryForm);
        toast.success('Country updated successfully');
      } else {
        await countriesAPI.create(countryForm);
        toast.success('Country added successfully');
      }
      setCountryDialog(false);
      setEditingCountry(null);
      setCountryForm({ name: '', code: '', vat_rate: 0 });
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save country');
    }
  };

  const handleEditCountry = (country) => {
    setEditingCountry(country);
    setCountryForm({
      name: country.name,
      code: country.code,
      vat_rate: country.vat_rate || 0
    });
    setCountryDialog(true);
  };

  const handleDeleteCountry = async (id) => {
    if (!window.confirm('Are you sure you want to delete this country?')) return;
    try {
      await countriesAPI.delete(id);
      toast.success('Country deleted');
      loadAllData();
    } catch (error) {
      toast.error('Failed to delete country');
    }
  };

  const handleExportOrders = () => {
    window.open(`${adminAPI.exportOrders()}?token=${token}`, '_blank');
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await settingsAPI.update({ contact_email: settings.contact_email });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // ==================== KNOWLEDGE MANAGEMENT FUNCTIONS ====================
  
  const CONTENT_TYPES = {
    press_release: 'Press Release',
    monthly_update: 'Monthly Update',
    quarterly_update: 'Quarterly Update',
    news: 'News',
    policy_brief: 'Policy Brief',
    guidance_note: 'Guidance Note',
    newsletter: 'Newsletter'
  };

  const CATEGORIES = {
    circular_economy: 'Circular Economy',
    epr_updates: 'EPR Updates',
    policy_brief: 'Policy Brief',
    newsletter: 'Newsletter',
    guidance: 'Guidance'
  };

  const handleSaveKnowledge = async () => {
    if (!knowledgeForm.title || !knowledgeForm.summary) {
      toast.error('Title and summary are required');
      return;
    }
    
    const API_BASE = process.env.REACT_APP_BACKEND_URL;
    try {
      if (editingKnowledge) {
        await fetch(`${API_BASE}/api/admin/knowledge/${editingKnowledge.id}`, {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}` 
          },
          body: JSON.stringify(knowledgeForm)
        });
        toast.success('Content updated');
      } else {
        await fetch(`${API_BASE}/api/admin/knowledge`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}` 
          },
          body: JSON.stringify(knowledgeForm)
        });
        toast.success('Content created');
      }
      
      setKnowledgeDialog(false);
      setEditingKnowledge(null);
      resetKnowledgeForm();
      loadKnowledgeContent();
    } catch (error) {
      toast.error('Failed to save content');
    }
  };

  const resetKnowledgeForm = () => {
    setKnowledgeForm({
      title: '',
      summary: '',
      body: '',
      content_type: 'news',
      category: '',
      publish_date: new Date().toISOString().split('T')[0],
      period_label: '',
      status: 'draft'
    });
  };

  const handleEditKnowledge = (item) => {
    setEditingKnowledge(item);
    setKnowledgeForm({
      title: item.title,
      summary: item.summary,
      body: item.body || '',
      content_type: item.content_type,
      category: item.category || '',
      publish_date: item.publish_date.split('T')[0],
      period_label: item.period_label || '',
      status: item.status
    });
    setKnowledgeDialog(true);
  };

  const handleDeleteKnowledge = async (id) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;
    
    const API_BASE = process.env.REACT_APP_BACKEND_URL;
    try {
      await fetch(`${API_BASE}/api/admin/knowledge/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Content deleted');
      loadKnowledgeContent();
    } catch (error) {
      toast.error('Failed to delete content');
    }
  };

  const handleUploadAttachment = async (contentId, file) => {
    const API_BASE = process.env.REACT_APP_BACKEND_URL;
    const formData = new FormData();
    formData.append('file', file);

    setUploadingAttachment(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/knowledge/${contentId}/attachment`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (response.ok) {
        toast.success('PDF uploaded successfully');
        loadKnowledgeContent();
      } else {
        toast.error('Failed to upload PDF');
      }
    } catch (error) {
      toast.error('Failed to upload PDF');
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleDeleteAttachment = async (contentId) => {
    const API_BASE = process.env.REACT_APP_BACKEND_URL;
    try {
      await fetch(`${API_BASE}/api/admin/knowledge/${contentId}/attachment`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Attachment removed');
      loadKnowledgeContent();
    } catch (error) {
      toast.error('Failed to remove attachment');
    }
  };

  const toggleKnowledgeStatus = async (item) => {
    const newStatus = item.status === 'published' ? 'draft' : 'published';
    const API_BASE = process.env.REACT_APP_BACKEND_URL;
    
    try {
      await fetch(`${API_BASE}/api/admin/knowledge/${item.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ status: newStatus })
      });
      toast.success(`Content ${newStatus === 'published' ? 'published' : 'unpublished'}`);
      loadKnowledgeContent();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#056839]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="admin-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#056839] flex items-center justify-center">
              <Settings className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#056839]">Admin Panel</h1>
              <p className="text-[#6B7280]">Manage services, tariffs, and orders</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8 bg-white">
            <TabsTrigger value="overview" data-testid="tab-overview">
              <TrendingUp className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services">
              <Package className="h-4 w-4 mr-2" />
              Services
            </TabsTrigger>
            <TabsTrigger value="tariffs" data-testid="tab-tariffs">
              <DollarSign className="h-4 w-4 mr-2" />
              Tariffs
            </TabsTrigger>
            <TabsTrigger value="companies" data-testid="tab-companies">
              <Building className="h-4 w-4 mr-2" />
              Companies
            </TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">
              <ShoppingCart className="h-4 w-4 mr-2" />
              Orders
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="knowledge" data-testid="tab-knowledge">
              <BookOpen className="h-4 w-4 mr-2" />
              Knowledge
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="animate-fade-in">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="admin-stat-orders">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#D2E4D6] flex items-center justify-center">
                    <ShoppingCart className="h-5 w-5 text-[#056839]" />
                  </div>
                  <span className="text-[#6B7280]">Total Orders</span>
                </div>
                <p className="text-3xl font-bold text-[#056839]">{stats?.total_orders || 0}</p>
              </div>
              
              <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="admin-stat-companies">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#D2E4D6] flex items-center justify-center">
                    <Users className="h-5 w-5 text-[#056839]" />
                  </div>
                  <span className="text-[#6B7280]">Companies</span>
                </div>
                <p className="text-3xl font-bold text-[#056839]">{stats?.total_companies || 0}</p>
              </div>
              
              <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="admin-stat-services">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#D2E4D6] flex items-center justify-center">
                    <Package className="h-5 w-5 text-[#056839]" />
                  </div>
                  <span className="text-[#6B7280]">Active Services</span>
                </div>
                <p className="text-3xl font-bold text-[#056839]">{stats?.total_services || 0}</p>
              </div>
              
              <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="admin-stat-revenue">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#E4D5B7] flex items-center justify-center">
                    <DollarSign className="h-5 w-5 text-[#A0522D]" />
                  </div>
                  <span className="text-[#6B7280]">Total Revenue</span>
                </div>
                <p className="text-3xl font-bold text-[#056839]">{formatCurrency(stats?.total_revenue || 0)}</p>
              </div>
            </div>
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services" className="animate-fade-in">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                <h2 className="text-lg font-semibold text-[#056839]">Services</h2>
                <Dialog open={serviceDialog} onOpenChange={setServiceDialog}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" onClick={() => { setEditingService(null); setServiceForm({ name: '', description: '', price: 0, category: 'compliance', features: '', is_calculator_based: false }); }} data-testid="add-service-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Service
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>{editingService ? 'Edit Service' : 'Add New Service'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label>Name</Label>
                        <Input value={serviceForm.name} onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })} className="mt-1" data-testid="service-name-input" />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Input value={serviceForm.description} onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })} className="mt-1" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Price (EUR)</Label>
                          <Input type="number" value={serviceForm.price} onChange={(e) => setServiceForm({ ...serviceForm, price: parseFloat(e.target.value) || 0 })} className="mt-1" />
                        </div>
                        <div>
                          <Label>Category</Label>
                          <Select value={serviceForm.category} onValueChange={(v) => setServiceForm({ ...serviceForm, category: v })}>
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="compliance">Compliance</SelectItem>
                              <SelectItem value="licensing">Licensing</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label>Features (one per line)</Label>
                        <textarea value={serviceForm.features} onChange={(e) => setServiceForm({ ...serviceForm, features: e.target.value })} className="mt-1 w-full h-24 rounded-md border border-input px-3 py-2 text-sm" />
                      </div>
                      <div className="flex items-center gap-2">
                        <input type="checkbox" id="calc-based" checked={serviceForm.is_calculator_based} onChange={(e) => setServiceForm({ ...serviceForm, is_calculator_based: e.target.checked })} />
                        <Label htmlFor="calc-based">Calculator-based pricing</Label>
                      </div>
                      <Button onClick={handleSaveService} className="w-full btn-primary" data-testid="save-service-btn">
                        {editingService ? 'Update Service' : 'Create Service'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full admin-table">
                  <thead>
                    <tr className="bg-[#D2E4D6]">
                      <th className="px-6 py-3 text-left text-sm font-semibold text-[#056839]">Name</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-[#056839]">Category</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-[#056839]">Price</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-[#056839]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.map((service, index) => (
                      <tr key={service.id} className="border-b border-gray-100 hover:bg-[#F9FAFB]" data-testid={`service-row-${index}`}>
                        <td className="px-6 py-4">
                          <p className="font-medium text-[#3F3F3F]">{service.name}</p>
                          <p className="text-sm text-[#6B7280] truncate max-w-xs">{service.description}</p>
                        </td>
                        <td className="px-6 py-4">
                          <Badge className="bg-[#D2E4D6] text-[#056839]">{service.category}</Badge>
                        </td>
                        <td className="px-6 py-4">
                          {service.is_calculator_based ? (
                            <span className="text-[#69A488]">Calculator</span>
                          ) : (
                            <span className="font-semibold text-[#056839]">{formatCurrency(service.price)}</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <Button variant="ghost" size="sm" onClick={() => handleEditService(service)} data-testid={`edit-service-${index}`}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteService(service.id)} data-testid={`delete-service-${index}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Tariffs Tab */}
          <TabsContent value="tariffs" className="animate-fade-in">
            <div className="space-y-6">
              {/* Countries Management */}
              <div className="bg-[#E4D5B7]/30 rounded-xl border border-gray-100 shadow-sm">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <Globe className="h-5 w-5 text-[#A0522D]" />
                    <h2 className="text-lg font-semibold text-[#056839]">Countries</h2>
                  </div>
                  <Dialog open={countryDialog} onOpenChange={setCountryDialog}>
                    <DialogTrigger asChild>
                      <Button className="btn-primary" onClick={() => { setEditingCountry(null); setCountryForm({ name: '', code: '', vat_rate: 0 }); }} data-testid="add-country-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Country
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{editingCountry ? 'Edit Country' : 'Add New Country'}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div>
                          <Label>Country Name</Label>
                          <Input 
                            value={countryForm.name} 
                            onChange={(e) => setCountryForm({ ...countryForm, name: e.target.value })} 
                            placeholder="e.g., Germany"
                            className="mt-1" 
                            data-testid="country-name-input"
                          />
                        </div>
                        <div>
                          <Label>Country Code (ISO 2-letter)</Label>
                          <Input 
                            value={countryForm.code} 
                            onChange={(e) => setCountryForm({ ...countryForm, code: e.target.value.toUpperCase() })} 
                            placeholder="e.g., DE"
                            maxLength={2}
                            className="mt-1" 
                            data-testid="country-code-input"
                          />
                        </div>
                        <div>
                          <Label>VAT Rate (%)</Label>
                          <Input 
                            type="number"
                            step="0.1"
                            value={countryForm.vat_rate} 
                            onChange={(e) => setCountryForm({ ...countryForm, vat_rate: parseFloat(e.target.value) || 0 })} 
                            placeholder="e.g., 19"
                            className="mt-1" 
                            data-testid="country-vat-input"
                          />
                          <p className="text-xs text-[#6B7280] mt-1">Enter the VAT percentage (e.g., 19 for 19%)</p>
                        </div>
                        <Button onClick={handleSaveCountry} className="w-full btn-primary" data-testid="save-country-btn">
                          {editingCountry ? 'Update Country' : 'Add Country'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                
                <div className="p-6">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="px-4 py-2 text-left text-sm font-semibold text-[#056839]">Country</th>
                          <th className="px-4 py-2 text-left text-sm font-semibold text-[#056839]">Code</th>
                          <th className="px-4 py-2 text-left text-sm font-semibold text-[#056839]">VAT Rate</th>
                          <th className="px-4 py-2 text-left text-sm font-semibold text-[#056839]">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {countries.map((country, index) => (
                          <tr key={country.id} className="border-b border-gray-100 hover:bg-[#F9FAFB]" data-testid={`country-row-${index}`}>
                            <td className="px-4 py-3 font-medium text-[#3F3F3F]">{country.name}</td>
                            <td className="px-4 py-3 text-[#6B7280]">{country.code}</td>
                            <td className="px-4 py-3 font-semibold text-[#056839]">{country.vat_rate || 0}%</td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => handleEditCountry(country)}
                                  data-testid={`edit-country-${index}`}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="text-red-500 hover:bg-red-50"
                                  onClick={() => handleDeleteCountry(country.id)}
                                  data-testid={`delete-country-${index}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {countries.length === 0 && (
                      <p className="text-[#6B7280] text-center py-4">No countries added yet. Add a country to start creating tariffs.</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Tariff Rates */}
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                  <div>
                    <h2 className="text-lg font-semibold text-[#056839]">Fee Management (USD per tonne)</h2>
                    <p className="text-sm text-[#6B7280] mt-1">Manage tariff rates for plastic packaging and WEEE materials</p>
                  </div>
                  <Dialog open={tariffDialog} onOpenChange={setTariffDialog}>
                    <DialogTrigger asChild>
                      <Button className="btn-primary" onClick={() => { setEditingTariff(null); setTariffForm({ country: countries[0]?.name || 'Zambia', category: 'plastic', material_type: 'PET - transparent/light', rate_per_tonne: '', year: 2026, is_pending: false }); }} data-testid="add-tariff-btn" disabled={countries.length === 0}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Tariff
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{editingTariff ? 'Edit Tariff' : 'Add New Tariff'}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Country</Label>
                            <Select value={tariffForm.country} onValueChange={(v) => setTariffForm({ ...tariffForm, country: v })}>
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder="Select country" />
                              </SelectTrigger>
                              <SelectContent>
                                {countries.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Year</Label>
                            <Select value={tariffForm.year.toString()} onValueChange={(v) => setTariffForm({ ...tariffForm, year: parseInt(v) })}>
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {YEARS.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Category</Label>
                            <Select 
                              value={tariffForm.category} 
                              onValueChange={(v) => setTariffForm({ 
                                ...tariffForm, 
                                category: v,
                                material_type: v === 'plastic' ? PLASTIC_MATERIAL_TYPES[0] : WEEE_MATERIAL_TYPES[0]
                              })}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="plastic">Plastic Packaging</SelectItem>
                                <SelectItem value="weee">WEEE (E-Waste)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Material Type</Label>
                            <Select value={tariffForm.material_type} onValueChange={(v) => setTariffForm({ ...tariffForm, material_type: v })}>
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {(tariffForm.category === 'plastic' ? PLASTIC_MATERIAL_TYPES : WEEE_MATERIAL_TYPES).map(m => (
                                  <SelectItem key={m} value={m}>{m}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            <input 
                              type="checkbox" 
                              id="is_pending"
                              checked={tariffForm.is_pending}
                              onChange={(e) => setTariffForm({ ...tariffForm, is_pending: e.target.checked, rate_per_tonne: e.target.checked ? '' : tariffForm.rate_per_tonne })}
                              className="h-4 w-4 rounded border-gray-300"
                            />
                            <Label htmlFor="is_pending" className="cursor-pointer">Fee pending (not yet published)</Label>
                          </div>
                          {!tariffForm.is_pending && (
                            <div>
                              <Label>Rate per tonne (USD)</Label>
                              <Input 
                                type="number" 
                                step="0.01" 
                                value={tariffForm.rate_per_tonne} 
                                onChange={(e) => setTariffForm({ ...tariffForm, rate_per_tonne: e.target.value })} 
                                className="mt-1"
                                placeholder="Enter rate in USD"
                              />
                            </div>
                          )}
                        </div>
                        <Button onClick={handleSaveTariff} className="w-full btn-primary" data-testid="save-tariff-btn">
                          {editingTariff ? 'Update Tariff' : 'Create Tariff'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                
                {/* Plastic Packaging Tariffs */}
                <div className="p-6 border-b border-gray-100">
                  <h3 className="text-md font-semibold text-[#056839] mb-4 flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    Plastic Packaging Fees
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full admin-table">
                      <thead>
                        <tr className="bg-[#D2E4D6]">
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#056839]">Material</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#056839]">Year</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#056839]">Rate/tonne (USD)</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#056839]">Status</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#056839]">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tariffs.filter(t => t.category === 'plastic' || !t.category).map((tariff, index) => (
                          <tr key={tariff.id} className="border-b border-gray-100 hover:bg-[#F9FAFB]" data-testid={`plastic-tariff-row-${index}`}>
                            <td className="px-4 py-3 text-[#3F3F3F]">{tariff.material_type}</td>
                            <td className="px-4 py-3 text-[#3F3F3F]">{tariff.year}</td>
                            <td className="px-4 py-3 font-semibold text-[#056839]">
                              {tariff.is_pending || tariff.rate_per_tonne === null ? '—' : formatCurrency(tariff.rate_per_tonne)}
                            </td>
                            <td className="px-4 py-3">
                              {tariff.is_pending ? (
                                <Badge className="bg-[#FEF3C7] text-[#92400E] hover:bg-[#FEF3C7]">Pending</Badge>
                              ) : (
                                <Badge className="bg-[#D2E4D6] text-[#056839] hover:bg-[#D2E4D6]">Published</Badge>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <Button variant="ghost" size="sm" onClick={() => handleEditTariff(tariff)} data-testid={`edit-plastic-tariff-${index}`}>
                                <Edit className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {tariffs.filter(t => t.category === 'plastic' || !t.category).length === 0 && (
                      <p className="text-[#6B7280] text-center py-4">No plastic packaging tariffs configured.</p>
                    )}
                  </div>
                </div>

                {/* WEEE Tariffs */}
                <div className="p-6">
                  <h3 className="text-md font-semibold text-[#A0522D] mb-4 flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    WEEE (E-Waste) Fees
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full admin-table">
                      <thead>
                        <tr className="bg-[#E4D5B7]/50">
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#A0522D]">Equipment Category</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#A0522D]">Year</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#A0522D]">Rate/tonne (USD)</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#A0522D]">Status</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#A0522D]">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tariffs.filter(t => t.category === 'weee').map((tariff, index) => (
                          <tr key={tariff.id} className="border-b border-gray-100 hover:bg-[#FEF9F0]" data-testid={`weee-tariff-row-${index}`}>
                            <td className="px-4 py-3 text-[#3F3F3F]">{tariff.material_type}</td>
                            <td className="px-4 py-3 text-[#3F3F3F]">{tariff.year}</td>
                            <td className="px-4 py-3 font-semibold text-[#A0522D]">
                              {tariff.is_pending || tariff.rate_per_tonne === null ? '—' : formatCurrency(tariff.rate_per_tonne)}
                            </td>
                            <td className="px-4 py-3">
                              {tariff.is_pending || tariff.rate_per_tonne === null ? (
                                <Badge className="bg-[#FEF3C7] text-[#92400E] hover:bg-[#FEF3C7]">Pending</Badge>
                              ) : (
                                <Badge className="bg-[#D2E4D6] text-[#056839] hover:bg-[#D2E4D6]">Published</Badge>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <Button variant="ghost" size="sm" onClick={() => handleEditTariff(tariff)} data-testid={`edit-weee-tariff-${index}`}>
                                <Edit className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {tariffs.filter(t => t.category === 'weee').length === 0 && (
                      <p className="text-[#6B7280] text-center py-4">No WEEE tariffs configured.</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Companies Tab */}
          <TabsContent value="companies" className="animate-fade-in">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-[#056839]">Registered Companies</h2>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full admin-table">
                  <thead>
                    <tr className="bg-[#D2E4D6]">
                      <th className="px-6 py-3 text-left text-sm font-semibold text-[#056839]">Company</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-[#056839]">Email</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-[#056839]">Role</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-[#056839]">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companies.map((company, index) => (
                      <tr key={company.id} className="border-b border-gray-100 hover:bg-[#F9FAFB]" data-testid={`company-row-${index}`}>
                        <td className="px-6 py-4 font-medium text-[#3F3F3F]">{company.company_name}</td>
                        <td className="px-6 py-4 text-[#6B7280]">{company.email}</td>
                        <td className="px-6 py-4">
                          <Badge className="bg-[#D2E4D6] text-[#056839]">{company.role}</Badge>
                        </td>
                        <td className="px-6 py-4 text-[#6B7280]">{formatShortDate(company.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders" className="animate-fade-in">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                <h2 className="text-lg font-semibold text-[#056839]">All Orders</h2>
                <Button variant="outline" className="btn-outline" onClick={handleExportOrders} data-testid="export-orders-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full admin-table">
                  <thead>
                    <tr className="bg-[#D2E4D6]">
                      <th className="px-6 py-3 text-left text-sm font-semibold text-[#056839]">Order #</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-[#056839]">Company</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-[#056839]">Service</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-[#056839]">Total</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-[#056839]">Status</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-[#056839]">Date</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-[#056839]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order, index) => (
                      <tr key={order.id} className="border-b border-gray-100 hover:bg-[#F9FAFB]" data-testid={`order-row-${index}`}>
                        <td className="px-6 py-4 font-medium text-[#056839]">{order.order_number}</td>
                        <td className="px-6 py-4 text-[#3F3F3F]">{order.company_details?.company_name}</td>
                        <td className="px-6 py-4 text-[#6B7280]">{order.items?.[0]?.service_name}</td>
                        <td className="px-6 py-4 font-semibold text-[#056839]">{formatCurrency(order.total)}</td>
                        <td className="px-6 py-4">
                          <Badge className={order.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-[#E4D5B7] text-[#A0522D]'}>
                            {order.status === 'confirmed' ? 'Confirmed' : 'Awaiting review'}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-[#6B7280]">{formatShortDate(order.created_at)}</td>
                        <td className="px-6 py-4">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-[#056839]" data-testid={`order-details-${index}`}>
                                <FileText className="h-4 w-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl">
                              <DialogHeader>
                                <DialogTitle>Order Details - {order.order_number}</DialogTitle>
                              </DialogHeader>
                              <OrderDetailPanel order={order} onUpdate={loadAllData} token={token} />
                            </DialogContent>
                          </Dialog>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="animate-fade-in">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-[#056839]">Settings</h2>
                <p className="text-sm text-[#6B7280] mt-1">Configure system settings for your PRO storefront</p>
              </div>
              
              <div className="p-6">
                {/* Contact Form Settings */}
                <div className="max-w-xl">
                  <h3 className="text-md font-semibold text-[#056839] mb-4 flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    Contact Form Settings
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <Label className="text-[#3F3F3F]">Contact Form Recipient Email</Label>
                      <p className="text-xs text-[#6B7280] mb-2">
                        All contact form submissions will be sent to this email address
                      </p>
                      <Input
                        type="email"
                        value={settings.contact_email || ''}
                        onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                        placeholder="v.nguyen@gfs.earth"
                        className="max-w-md"
                        data-testid="contact-email-setting"
                      />
                    </div>
                    
                    <Button 
                      onClick={handleSaveSettings}
                      className="bg-[#056839] text-white hover:bg-[#04522d]"
                      disabled={savingSettings}
                      data-testid="save-settings-btn"
                    >
                      {savingSettings ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="mr-2 h-4 w-4" />
                          Save Settings
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Knowledge Management Tab */}
          <TabsContent value="knowledge" className="animate-fade-in">
            <div className="space-y-6">
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                  <div>
                    <h2 className="text-lg font-semibold text-[#056839]">Knowledge Management</h2>
                    <p className="text-sm text-[#6B7280]">Create and manage content for the producer Knowledge Hub</p>
                  </div>
                  <Dialog open={knowledgeDialog} onOpenChange={setKnowledgeDialog}>
                    <DialogTrigger asChild>
                      <Button className="btn-primary" onClick={() => { setEditingKnowledge(null); resetKnowledgeForm(); }} data-testid="add-knowledge-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Create Content
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>{editingKnowledge ? 'Edit Content' : 'Create New Content'}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div>
                          <Label>Title *</Label>
                          <Input
                            value={knowledgeForm.title}
                            onChange={(e) => setKnowledgeForm({ ...knowledgeForm, title: e.target.value })}
                            placeholder="Enter title"
                            className="mt-1"
                          />
                        </div>
                        
                        <div>
                          <Label>Summary *</Label>
                          <textarea
                            value={knowledgeForm.summary}
                            onChange={(e) => setKnowledgeForm({ ...knowledgeForm, summary: e.target.value })}
                            placeholder="Brief summary for the card preview"
                            rows={2}
                            className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                          />
                        </div>
                        
                        <div>
                          <Label>Full Content (optional)</Label>
                          <textarea
                            value={knowledgeForm.body}
                            onChange={(e) => setKnowledgeForm({ ...knowledgeForm, body: e.target.value })}
                            placeholder="Full article content..."
                            rows={8}
                            className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                          />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Content Type</Label>
                            <Select value={knowledgeForm.content_type} onValueChange={(v) => setKnowledgeForm({ ...knowledgeForm, content_type: v })}>
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(CONTENT_TYPES).map(([key, label]) => (
                                  <SelectItem key={key} value={key}>{label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div>
                            <Label>Category (optional)</Label>
                            <Select value={knowledgeForm.category || 'none'} onValueChange={(v) => setKnowledgeForm({ ...knowledgeForm, category: v === 'none' ? '' : v })}>
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder="Select category" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">No Category</SelectItem>
                                {Object.entries(CATEGORIES).map(([key, label]) => (
                                  <SelectItem key={key} value={key}>{label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Publish Date</Label>
                            <Input
                              type="date"
                              value={knowledgeForm.publish_date}
                              onChange={(e) => setKnowledgeForm({ ...knowledgeForm, publish_date: e.target.value })}
                              className="mt-1"
                            />
                          </div>
                          
                          <div>
                            <Label>Period Label (optional)</Label>
                            <Input
                              value={knowledgeForm.period_label}
                              onChange={(e) => setKnowledgeForm({ ...knowledgeForm, period_label: e.target.value })}
                              placeholder="e.g., Q1 2026, March 2026"
                              className="mt-1"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <Label>Status</Label>
                          <Select value={knowledgeForm.status} onValueChange={(v) => setKnowledgeForm({ ...knowledgeForm, status: v })}>
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="draft">Draft</SelectItem>
                              <SelectItem value="published">Published</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <Button onClick={handleSaveKnowledge} className="w-full btn-primary">
                          {editingKnowledge ? 'Update Content' : 'Create Content'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                
                <div className="overflow-x-auto">
                  {knowledgeContent.length === 0 ? (
                    <div className="p-12 text-center">
                      <BookOpen className="h-12 w-12 text-[#D2E4D6] mx-auto mb-4" />
                      <p className="text-[#6B7280]">No content created yet</p>
                      <p className="text-sm text-[#9CA3AF]">Create your first knowledge article above</p>
                    </div>
                  ) : (
                    <table className="w-full admin-table">
                      <thead>
                        <tr className="bg-[#D2E4D6]">
                          <th className="px-6 py-3 text-left text-sm font-semibold text-[#056839]">Title</th>
                          <th className="px-6 py-3 text-left text-sm font-semibold text-[#056839]">Type</th>
                          <th className="px-6 py-3 text-left text-sm font-semibold text-[#056839]">Status</th>
                          <th className="px-6 py-3 text-left text-sm font-semibold text-[#056839]">Date</th>
                          <th className="px-6 py-3 text-left text-sm font-semibold text-[#056839]">PDF</th>
                          <th className="px-6 py-3 text-left text-sm font-semibold text-[#056839]">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {knowledgeContent.map((item, index) => (
                          <tr key={item.id} className="border-b border-gray-100 hover:bg-[#F9FAFB]" data-testid={`knowledge-row-${index}`}>
                            <td className="px-6 py-4">
                              <p className="font-medium text-[#056839] line-clamp-1">{item.title}</p>
                              <p className="text-xs text-[#6B7280] line-clamp-1">{item.summary}</p>
                            </td>
                            <td className="px-6 py-4">
                              <Badge className="bg-blue-100 text-blue-700">
                                {CONTENT_TYPES[item.content_type] || item.content_type}
                              </Badge>
                            </td>
                            <td className="px-6 py-4">
                              <Badge 
                                className={item.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}
                              >
                                {item.status === 'published' ? 'Published' : 'Draft'}
                              </Badge>
                            </td>
                            <td className="px-6 py-4 text-[#6B7280] text-sm">{item.publish_date?.split('T')[0]}</td>
                            <td className="px-6 py-4">
                              {item.attachment ? (
                                <div className="flex items-center gap-2">
                                  <Badge className="bg-red-100 text-red-700">PDF</Badge>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => handleDeleteAttachment(item.id)}
                                    className="text-red-500 h-6 w-6 p-0"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              ) : (
                                <label className="cursor-pointer">
                                  <input
                                    type="file"
                                    accept="application/pdf"
                                    className="hidden"
                                    onChange={(e) => e.target.files[0] && handleUploadAttachment(item.id, e.target.files[0])}
                                  />
                                  <span className="text-xs text-[#056839] hover:underline flex items-center gap-1">
                                    <Upload className="h-3 w-3" /> Upload
                                  </span>
                                </label>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => toggleKnowledgeStatus(item)}
                                  title={item.status === 'published' ? 'Unpublish' : 'Publish'}
                                >
                                  {item.status === 'published' ? (
                                    <EyeOff className="h-4 w-4 text-[#6B7280]" />
                                  ) : (
                                    <Eye className="h-4 w-4 text-[#056839]" />
                                  )}
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleEditKnowledge(item)}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteKnowledge(item.id)} className="text-red-500">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
